import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { useToast } from "../components/ui/Toast";
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { Spinner } from "../components/ui/Spinner";
import AgentForm, { AgentFormRef } from './AgentForm';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';

export const EditAgentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const agentsApi = useAgentsApi();
  const [editedAgent, setEditedAgent] = useState<Agent | null>(null);
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null);
  const initialFetchMade = useRef(false);
  const [existingAgents, setExistingAgents] = useState<Agent[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const { 
    showNavigationWarning, 
    setShowNavigationWarning,
    pendingNavigation,
    setPendingNavigation
  } = useNavigation();
  const formRef = useRef<AgentFormRef>(null);
  
  const hasUnsavedChanges = 
    JSON.stringify(editedAgent) !== JSON.stringify(originalAgent);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        setShowNavigationWarning(true);
        setPendingNavigation(null);
        // Push the state back to keep the user on the page
        window.history.pushState(null, '', window.location.pathname);
        event.preventDefault();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, setShowNavigationWarning, setPendingNavigation]);

  // Handle navigation when user confirms
  useEffect(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate]);

  useEffect(() => {
    const fetchAgent = async () => {
      if (!id || initialFetchMade.current) return;
      
      try {
        initialFetchMade.current = true;
        
        // Fetch the agent data
        const agentData = await agentsApi.getAgent(id);
        setEditedAgent(agentData);
        setOriginalAgent(JSON.parse(JSON.stringify(agentData)));
        
        // Fetch all agents to check for name conflicts
        const allAgents = await agentsApi.getAgents();
        // Store all agents for validation
        setExistingAgents(allAgents);
        
      } catch (error) {
        console.error('Error fetching agent:', error);
        addToast({
          title: 'Error',
          description: 'Failed to load agent. Please try again.',
          variant: 'destructive',
        });
        navigate('/studio/agents');
      }
    };

    fetchAgent();
  }, [id, addToast, navigate]);

  const handleSave = async () => {
    if (!editedAgent) return;
    
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Check if the agent name already exists (excluding the current agent)
      if (existingAgents.some(agent => 
        agent.agentName?.toLowerCase() === editedAgent.agentName?.toLowerCase() && 
        agent.agentId !== editedAgent.agentId
      )) {
        addToast({
          title: 'Error',
          description: 'An agent with this name already exists',
          variant: 'destructive',
        });
        return;
      }
      
      // Save the agent
      await agentsApi.updateAgent(editedAgent);
      
      // Update the original agent to match the saved state
      setOriginalAgent(JSON.parse(JSON.stringify(editedAgent)));
      
      addToast({
        title: 'Success',
        description: 'Agent saved successfully',
        variant: 'default',
      });
      
      // Navigate back to agents list
      navigate('/studio/agents');
      
    } catch (error) {
      console.error('Error saving agent:', error);
      addToast({
        title: 'Error',
        description: 'Failed to save agent. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/agents');
    } else {
      navigate('/studio/agents');
    }
  };

  if (!editedAgent && initialFetchMade.current) {
    return (
      <div className="container mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Agent not found</p>
        </div>
      </div>
    );
  }

  if (!editedAgent) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-purple-500" />
        <p className="text-center text-muted-foreground">Loading agent details...</p>
      </div>
    );
  }

  return (
    <>
      <AgentForm
        ref={formRef}
        agent={editedAgent}
        onAgentChange={setEditedAgent}
        onSubmit={handleSave}
        onCancel={handleCancel}
        isEditing={true}
        existingAgents={existingAgents}
        isSaving={isSaving}
      />
      
      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditAgentPage;